import { RouteProp, useNavigation } from "@react-navigation/native";
import React from "react";
import { useLazyLoadQuery, graphql, useMutation } from "react-relay";
import {
  UserLoggedInNavigatorParamList,
  UserLoggedInNavigatorNavigationProp,
} from "../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";

import { AHInput, AHText, Box, useTheme } from "arkh";

import { FileIcon } from "./FileListItem/FileIcon";
import { FileInfoScreenQuery } from "./__generated__/FileInfoScreenQuery.graphql";
import { FileInfoScreenUpdateNameMutation } from "./__generated__/FileInfoScreenUpdateNameMutation.graphql";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";

import { FormattedMessage } from "react-intl";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { FormattedDateInWorkspace } from "../../common/dates/FormattedDate";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";

type ParamList = {
  FileInfoScreen: UserLoggedInNavigatorParamList["FileInfo"];
};
const Stack = createNativeStackNavigator<ParamList>();
export const FileInfoScreen = withWebModal(
  (props: {
    route: RouteProp<UserLoggedInNavigatorParamList, "FileInfo">;
    navigation: UserLoggedInNavigatorNavigationProp<"FileInfo">;
  }) => {
    const theme = useTheme();
    return (
      <Stack.Navigator>
        <Stack.Screen
          name="FileInfoScreen"
          component={FileInfoScreenC}
          initialParams={props.route.params}
          options={{
            headerTitle: "",
            headerShadowVisible: false,
            headerStyle: {
              backgroundColor: theme.background,
            },
          }}
        />
      </Stack.Navigator>
    );
  },
  true
);

function FileInfoScreenC(props: {
  route: RouteProp<ParamList, "FileInfoScreen">;
}) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fk) => <FileInfo {...props} fetchKey={fk} />}
    </LazyQueryNetworkBoundrary>
  );
}

const DetailRow = (props: {
  desc: React.ReactNode;
  content: React.ReactNode;
}) => {
  return (
    <Box
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
        marginVertical: 3,
      }}
    >
      <Box>
        <AHText color="darkerText">{props.desc}</AHText>
      </Box>
      <Box>
        <AHText>{props.content}</AHText>
      </Box>
    </Box>
  );
};

function FileInfo(props: {
  route: RouteProp<ParamList, "FileInfoScreen">;
  fetchKey: string;
}) {
  const { file } = useLazyLoadQuery<FileInfoScreenQuery>(
    graphql`
      query FileInfoScreenQuery($id: ID!) {
        file(id: $id) {
          type
          name
          pdfUrl
          status
          formattedSize

          createdAtDate {
            ...FormattedDate
          }
          lastAccessedAtDate {
            ...FormattedDate
          }

          uploadedBy {
            name
          }
          accessedBy {
            name
            id
            email
          }
          accessedCount
        }
      }
    `,
    {
      id: props.route.params.id,
    },
    {
      fetchPolicy: "store-and-network",
      fetchKey: props.fetchKey,
    }
  );

  const [fileName, setFileName] = React.useState(file?.name ?? "");

  const [updateName, isUpdatingName] =
    useMutation<FileInfoScreenUpdateNameMutation>(graphql`
      mutation FileInfoScreenUpdateNameMutation($id: ID!, $name: String!) {
        file(id: $id) {
          update(input: { setName: $name }) {
            id
            name
          }
        }
      }
    `);

  const intl_tokens = useIntlTokens();

  const navigation = useNavigation();

  useNavigationHeader({
    type: "SAVE",
    onCancel: () => navigation.goBack(),
    onSave: () => {
      updateName({
        variables: {
          id: props.route.params.id,
          name: fileName,
        },
        onCompleted: () => {
          navigation.goBack();
        },
      });
    },
    title: intl_tokens.Name,
    defaultContentStyle: true,
    saveLoading: isUpdatingName,
    disabled: fileName.length === 0,
  });

  if (!file) return null;
  return (
    <KeyboardAwareScrollView>
      <Box spaceTop="large" space="large">
        <AHText type="title-small" spaceBottom="small" spaceHorizontal="medium">
          {intl_tokens.Name}
        </AHText>
        <Box
          style={{ flexDirection: "row" }}
          spaceTop="xsmall"
          spaceBottom="large"
        >
          <AHInput
            defaultStyle
            style={{
              flex: 1,
            }}
            onChangeText={(newVal) => {
              setFileName(newVal);
            }}
            maxLength={250}
            value={fileName ?? ""}
          />
        </Box>

        <Box
          color="surface"
          cornerRadius="large"
          style={{ position: "relative", overflow: "hidden" }}
        >
          <Box
            style={{
              flexDirection: "row",
              alignItems: "center",
            }}
            space="small"
          >
            <FileIcon filetype={file.type} size={70} />
            <Box space="medium" style={{ flex: 1, justifyContent: "center" }}>
              <DetailRow
                desc={
                  <FormattedMessage
                    description="FileInfoScreen File Size Label"
                    defaultMessage="Size"
                  />
                }
                content={file.formattedSize}
              />
              <DetailRow
                desc={
                  <FormattedMessage
                    description="FileInfoScreen File Uploaded At Label"
                    defaultMessage="Uploaded"
                  />
                }
                content={<FormattedDateInWorkspace date={file.createdAtDate} />}
              />
              <DetailRow
                desc={
                  <FormattedMessage
                    description="FileInfoScreen File Uploaded By Label"
                    defaultMessage="Uploaded By"
                  />
                }
                content={file.uploadedBy?.name ?? ""}
              />
              <DetailRow
                desc={
                  <FormattedMessage
                    description="FileInfoScreen File Last Opened Label"
                    defaultMessage="Last Opened"
                  />
                }
                content={
                  file.lastAccessedAtDate ? (
                    <FormattedDateInWorkspace date={file.lastAccessedAtDate} />
                  ) : (
                    intl_tokens.Never
                  )
                }
              />
            </Box>
          </Box>
          {file.accessedBy.length > 0 ? (
            <Box
              space="large"
              borderColor="border-primary"
              style={{ borderTopWidth: 1 }}
            >
              <AHText spaceBottom="xxsmall" type="subtitle">
                <FormattedMessage
                  description="FileInfoScreen File Opened Label"
                  defaultMessage="Opened"
                />
              </AHText>
              <AHText spaceBottom="xsmall" type="detail">
                <FormattedMessage
                  description="FileInfoScreen File Uploaded At Label"
                  defaultMessage="File has been opened {accessedCount} times by {accessUsersCount} users."
                  values={{
                    accessedCount: file.accessedCount,
                    accessUsersCount: file.accessedBy.length,
                  }}
                />
              </AHText>
              {file.accessedBy.map((af) => (
                <Box
                  key={af.id}
                  style={{
                    justifyContent: "space-between",
                    flexDirection: "row",
                  }}
                  spaceTop="xxsmall"
                >
                  <AHText color="darkerText">{af.name}</AHText>
                  <AHText>{af.email}</AHText>
                </Box>
              ))}
            </Box>
          ) : null}
        </Box>
      </Box>
    </KeyboardAwareScrollView>
  );
}
